import has from 'lodash/has';
import isEqual from 'lodash/isEqual';
import isArray from 'lodash/isArray';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import minBy from 'lodash/minBy';
import { ACLQueryModeTypes } from '../constants/visibilityFilters';

const ROLE_RANKS = {
  owner: 0,
  admin: 1,
  operator: 2,
  collaborator: 3,
  sharer: 4,
};

export const getEffectiveRole = (data = {}) => {
  if (has(data, 'access_roles') && isArray(data.access_roles)) {
    // return the most permitting role
    return minBy(data.access_roles, (role) => ROLE_RANKS[role]);
  }

  return null;
};

export const isReadOnly = (data = {}) => {
  let isReadOnly = false;

  if (
    has(data, 'access_roles') &&
    isArray(data.access_roles) &&
    (isEqual(['collaborator'], data.access_roles) || isEmpty(data.access_roles))
  ) {
    isReadOnly = true;
  }

  return isReadOnly;
};

export const isAdminAccess = (data = {}) => {
  let hasAdminPermission = false;

  if (
    has(data, 'access_roles') &&
    isArray(data.access_roles) &&
    isEqual(['admin'], data.access_roles)
  ) {
    hasAdminPermission = true;
  }

  return hasAdminPermission;
};

export function hasAccessRole(data = {}, role) {
  return data?.access_roles?.includes(role);
}

export function isOperator(data) {
  return hasAccessRole(data, 'operator');
}

export function isCollaborator(data) {
  return hasAccessRole(data, 'collaborator');
}

export const isOwner = (data = {}) => {
  return data?.access_roles?.includes('owner');
};

export const isManagedResource = (data = {}) => {
  let isManagedResource = false;

  if (has(data, 'managed') && data.managed === true) {
    isManagedResource = true;
  }

  return isManagedResource;
};

export const doesUserOwnResource = (data = {}, currentUser) => {
  let ownsResource = false;

  if (
    data.owner === currentUser.id ||
    data.owner_id === currentUser.id ||
    (has(data, 'access_roles') &&
      isArray(data.access_roles) &&
      includes(data.access_roles, 'owner'))
  ) {
    ownsResource = true;
  }
  return ownsResource;
};

export const shouldRefetchForAclMode = (aclQueryMode = ACLQueryModeTypes.MODE_OWNER.key) => {
  return true;
};

export const shouldDisplayOwnershipLabel = (aclQueryMode = ACLQueryModeTypes.MODE_OWNER.key) => {
  return aclQueryMode === ACLQueryModeTypes.MODE_COLLABORATOR.key;
};

const restrictedActionsPerRole = {
  operator: ['Pause', 'Activate'],
  collaborator: [], // collaborator is not allowed to perform any action
};

export const restrictMenuForRole = (menuItems, data) => {
  const role = getRole(data);
  const restrictedActions = restrictedActionsPerRole[role];
  return menuItems.map((menu) => {
    if (!restrictedActions || restrictedActions.some((action) => menu.label === action)) {
      return menu;
    }
    return {
      ...menu,
      disable: true,
    };
  });
};

export const getRole = (data) => {
  return data?.access_roles?.[0];
};

export const ownershipLabel = (data = false, isAdminAccess = false) => {
  let readOnly;
  if (typeof data !== 'boolean') {
    const role = getRole(data);
    const roles = {
      admin: 'Administrator',
      owner: '',
      collaborator: 'Collaborator',
      operator: 'Operator',
    };
    return roles[role] || '';
  }
  // Preserving this to make it work in other places
  readOnly = data;
  if (readOnly) {
    return 'Collaborator';
  }
  if (isAdminAccess) {
    return 'Administrator';
  }
  return '';
};

export function hasPermissionToEditSource(data = {}) {
  return hasAccessRole(data, 'admin') || hasAccessRole(data, 'owner');
}
